'use client';

import { useCallback } from 'react';
import { useAppDispatch } from '@/app/store/hooks';
import { toggleFavorite } from '@/app/store/slices/userAppAccessSlice';

/**
 * Hook to flip the favorite flag for a SysApp. Unlike useTrackAppAccess, NO
 * client-side dedupe — favorite toggling is a deliberate user action, not an
 * incidental side-effect of navigation, so legitimate rapid toggles should
 * reach the server. Server upsert is idempotent-to-target-state.
 *
 * Returns a function that resolves with the server-confirmed post-toggle
 * `isFavorite`, or rejects with the error message. Call-site decides whether
 * to await (to surface a toast) or fire-and-forget.
 */
export function useToggleFavorite() {
  const dispatch = useAppDispatch();

  return useCallback(
    async (sysAppId: number): Promise<boolean> => {
      if (typeof window === 'undefined') {
        return Promise.reject(new Error('toggleFavorite called during SSR'));
      }
      if (!sysAppId || sysAppId <= 0) {
        return Promise.reject(new Error('Invalid sysAppId'));
      }

      const result = await dispatch(toggleFavorite(sysAppId)).unwrap();
      return result.isFavorite;
    },
    [dispatch]
  );
}
